<template>
  <div class="otp-input">
    <div class="otp-input__fields">
      <input
        v-for="(digit, index) in digits"
        :key="index"
        :ref="`input-${index}`"
        v-model="digits[index]"
        :disabled="disabled"
        :maxlength="1"
        :autofocus="index === 0"
        class="otp-input__field"
        type="text"
        inputmode="numeric"
        @input="onInput(index, $event)"
        @keydown="onKeyDown(index, $event)"
        @paste="onPaste($event)"
      />
    </div>
  </div>
</template>

<script>
export default {
  name: 'OtpInput',

  props: {
    length: {
      type: Number,
      default: 6,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      digits: new Array(this.length).fill(''),
    }
  },

  computed: {
    otpValue() {
      return this.digits.join('')
    },
  },

  watch: {
    otpValue(newValue) {
      this.onChange(newValue)
    },
  },

  methods: {
    onChange(value) {
      this.$emit('change', value)

      if (value.length === this.length) {
        this.$emit('complete', value)
      }
    },

    onInput(index, event) {
      // Оставляем только цифры
      const digit = event.target.value.replace(/\D/g, '').slice(-1)
      this.$set(this.digits, index, digit)

      // Переходим к следующему полю
      if (digit && index < this.length - 1) {
        this.focusInput(index + 1)
      }
    },

    onKeyDown(index, event) {
      // Backspace - переходим к предыдущему полю
      if (event.key === 'Backspace' && !this.digits[index] && index > 0) {
        this.focusInput(index - 1)
      }

      // ArrowLeft
      if (event.key === 'ArrowLeft' && index > 0) {
        event.preventDefault()
        this.focusInput(index - 1)
      }

      // ArrowRight
      if (event.key === 'ArrowRight' && index < this.length - 1) {
        event.preventDefault()
        this.focusInput(index + 1)
      }
    },

    onPaste(event) {
      event.preventDefault()
      const pastedData = event.clipboardData.getData('text/plain')
      const digits = pastedData.replace(/\D/g, '').slice(0, this.length).split('')

      for (let i = 0; i < this.length; i++) {
        this.$set(this.digits, i, digits[i] || '')
      }

      // Фокусируемся на первом пустом поле или последнем заполненном
      const emptyIndex = this.digits.findIndex((digit) => !digit)
      const targetIndex = emptyIndex >= 0 ? emptyIndex : this.length - 1
      this.focusInput(targetIndex)
    },

    focusInput(index) {
      this.$nextTick(() => {
        const input = this.$refs[`input-${index}`]
        if (input && input[0]) {
          input[0].focus()
        }
      })
    },

    clear() {
      this.digits = new Array(this.length).fill('')
      this.focusInput(0)
    },
  },
}
</script>

<style lang="scss" scoped>
.otp-input {
  width: 100%;

  &__fields {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 12px;

    @media (max-width: 480px) {
      gap: 8px;
    }
  }

  &__field {
    width: 60px;
    height: 60px;
    border: 2px solid #fff;
    border-radius: 8px;
    background: #fff;
    text-align: center;
    font-size: 18px;
    font-weight: 400;
    outline: none;
    transition: all 0.2s ease-in-out;

    &:focus {
      border-color: #1b559e;
    }

    &:hover:not(:focus) {
      border-color: #666;
    }

    &:disabled {
      background-color: #f5f5f5;
      color: #999;
      cursor: not-allowed;
    }

    @media (max-width: 480px) {
      font-size: 16px;
    }
  }
}

// Адаптивность для маленьких экранов
@media (max-width: 480px) {
  .otp-input {
    &__field {
      width: 50px;
      height: 50px;
      font-size: 16px;
    }
  }
}

// Адаптивность для очень маленьких экранов
@media (max-width: 320px) {
  .otp-input {
    &__fields {
      gap: 6px;
    }

    &__field {
      width: 40px;
      height: 40px;
      font-size: 14px;
    }
  }
}
</style>
